import { useEffect, useState } from 'react';
import { linearRegression } from '../../../../util/util';

function fixName(incoming) {
  const fixed = [];
  fixed.ewmaucl = '+1.5 Sigma';
  fixed.ewmalcl = '-1.5 Sigma';
  fixed.ewmatrend = 'Trend';
  fixed.average = 'Average';
  fixed.result = 'Data';
  fixed.mr = 'Data';
  fixed.mravg = 'Average';
  fixed.mrucl = 'UCL';
  fixed.ucl = 'UCL';
  fixed.lcl = 'LCL';
  fixed.pp = 'Data';
  fixed.id = 'Round';
  fixed.zval = 'Quantiles';
  if (fixed[incoming]) {
    return fixed[incoming];
  }
  return incoming;
}

function mapToChart(megaChartData, chartData) {
  const xAxisKey = megaChartData[0]?.keyX || 'Round';
  const headers = [xAxisKey, ...megaChartData.map((item) => item.key)];

  const xAxis = (i) => {
    const xFinder = ['Result', 'Data'];

    const x = megaChartData.find((item) => xFinder.includes(item.key))?.values?.[i]?.x;
    return chartData.find((item) => item.id === x)?.round || x;
  };

  const newArray = [...Array(megaChartData?.[0]?.values?.length || 0).keys()].map((index) => {
    return [xAxis(index), ...megaChartData.map((item) => item.values[index]?.y ?? null)];
  });
  return [headers, ...newArray];
}

function getMegaChartData({
  includeAll,
  trendLine,
  xDataField,
  yDataField,
  yDataField2,
  yDataField3,
  yDataField4,
  yDataField5,
  yDataField6,
  yDataField7,
  color2,
  color3,
  color4,
  color5,
  color6,
  color7,
  data: chartData
}) {
  const thedata = [];
  const thedatamore = [];
  const thedatamorenames = [];
  const thedatamorecolors = [];
  const maxFieldCount = 7;
  for (let idx = 0; idx <= maxFieldCount; idx += 1) {
    thedatamore.push([]);
  }
  thedatamorenames[2] = yDataField2;
  thedatamorenames[3] = yDataField3;
  thedatamorenames[4] = yDataField4;
  thedatamorenames[5] = yDataField5;
  thedatamorenames[6] = yDataField6;
  thedatamorenames[7] = yDataField7;
  thedatamorecolors[2] = color2;
  thedatamorecolors[3] = color3;
  thedatamorecolors[4] = color4;
  thedatamorecolors[5] = color5;
  thedatamorecolors[6] = color6;
  thedatamorecolors[7] = color7;

  // Data is represented as an array of {x,y} pairs.
  if (typeof chartData === 'undefined') {
    return undefined;
  }

  const activeArray = chartData;
  const allX = [];
  const allY = [];
  let minX = 9999;
  let maxX = -99999;

  const startIndex = 0;

  for (let i = 0; i < activeArray.length; i += 1) {
    const dataObj = activeArray[i];
    if (includeAll || dataObj.omit === 'N') {
      const xVal = dataObj[xDataField];
      const yVal = dataObj[yDataField];
      const yValArr = [];
      for (let idx = startIndex; idx <= maxFieldCount; idx += 1) {
        const fieldName = thedatamorenames[idx];
        if (dataObj[fieldName] !== '') {
          yValArr[idx] = dataObj[fieldName];
        }
      }

      if (
        yVal !== undefined &&
        yVal !== '' &&
        !Number.isNaN(yVal) &&
        xVal !== undefined &&
        xVal !== '' &&
        !Number.isNaN(xVal)
      ) {
        if (includeAll && dataObj.outlier > 0 && xDataField !== 'zScoreAll') {
          thedata.push({
            y: yVal,
            x: xVal,
            shape: 'triangle-down',
            size: 100
          });
        } else if (includeAll && dataObj.omit === 'Y' && xDataField !== 'zScoreAll') {
          thedata.push({
            y: yVal,
            x: xVal,
            shape: 'triangle-up',
            size: 100
          });
        } else {
          thedata.push({
            y: yVal,
            x: xVal
          });
        }
        for (let idx = startIndex; idx <= maxFieldCount; idx += 1) {
          if (typeof yValArr[idx] === 'number' && !Number.isNaN(yValArr[idx])) {
            thedatamore[idx].push({
              y: yValArr[idx],
              x: xVal
            });
          }
        }
        allY.push(yVal);
        allX.push(xVal);
        if (xVal > maxX) maxX = xVal;
        if (xVal < minX) minX = xVal;
      }
    }
  }

  const returnArray = [
    {
      values: thedata,
      type: 'scatter',
      key: fixName(yDataField),
      keyX: fixName(xDataField)
    }
  ];

  // if we have additional y values:
  for (let idx = startIndex; idx <= maxFieldCount; idx += 1) {
    if (typeof thedatamore[idx] !== 'undefined' && thedatamore[idx].length > 0) {
      const displayName = fixName(thedatamorenames[idx]);
      returnArray.push({
        values: thedatamore[idx], // values - represents the array of {x,y} data points
        key: displayName, // key  - the name of the series.
        // color: d3.rgb(thedatamorecolors[idx]),
        type: 'line'
      });
    } // thedatamore[idx].length > 0
  }

  if (trendLine) {
    // run regression and create trend line:
    const lr = linearRegression(allY, allX);
    const numPts = 100;
    const trendData = new Array(numPts);
    for (let idx = 0; idx < numPts; idx += 1) {
      trendData[idx] = {
        x: minX + (maxX - minX) * (idx / numPts),
        y: lr.slope * (minX + ((maxX - minX) * idx) / numPts) + lr.intercept
      };
    }
    returnArray.push({
      values: trendData,
      type: 'line',
      key: 'Trend',
      color: '#ff7f0e'
    });
  } // if trendLine

  return mapToChart(returnArray, chartData);
} // megaChartData

const usePlotCharts = () => {
  const [mrChartAllData, setMrChartAllData] = useState([]);
  const [iChartAllDataILCP, setIChartAllDataILCP] = useState([]);
  const [qqChartAllData, setQqChartAllData] = useState([]);
  const [data, setData] = useState([]);
  useEffect(() => {
    setMrChartAllData(
      getMegaChartData({
        includeAll: true,
        trendLine: false,
        xDataField: 'id',
        yDataField: 'mr',
        yDataField2: 'mravg',
        yDataField3: 'mrucl',
        color1: 'green',
        color2: 'orange',
        color3: 'orange',
        data
      })
    );
    setIChartAllDataILCP(
      getMegaChartData({
        includeAll: true,
        trendLine: false,
        xDataField: 'id',
        yDataField: 'result',
        yDataField2: 'average',
        yDataField3: 'ewmatrend',
        yDataField4: 'ucl',
        yDataField5: 'lcl',
        yDataField6: 'ewmaucl',
        yDataField7: 'ewmalcl',
        color1: 'green',
        color2: 'orange',
        color3: 'blue',
        color4: 'orange',
        color5: 'orange',
        color6: 'purple',
        color7: 'purple',
        data
      })
    );
    setQqChartAllData(
      getMegaChartData({
        includeAll: true,
        trendLine: false,
        xDataField: 'zval',
        yDataField: 'pp',
        color1: 'green',
        data
      })
    );
  }, [data]);

  return {
    iChartAllDataILCP,
    mrChartAllData,
    qqChartAllData,
    setData,
    setMrChartAllData,
    setIChartAllDataILCP,
    setQqChartAllData
  };
};

export default usePlotCharts;
